import { useState, useCallback } from 'react';

/**
 * Hook to manage custom confirmation modal state
 * Replaces window.confirm() with branded DIZEE modal
 * 
 * Usage:
 * const { confirmState, showConfirm, ConfirmDialog } = useConfirm();
 * 
 * const handleDelete = async () => {
 *   const confirmed = await showConfirm({
 *     title: 'Delete Item',
 *     message: 'Are you sure you want to delete this?',
 *     confirmLabel: 'Delete',
 *   });
 *   if (confirmed) {
 *     // proceed with delete
 *   }
 * };
 * 
 * return (
 *   <>
 *     {ConfirmDialog}
 *     <button onClick={handleDelete}>Delete</button>
 *   </>
 * );
 */
export const useConfirm = () => {
    const [confirmState, setConfirmState] = useState({
        isOpen: false,
        title: '',
        message: '',
        confirmLabel: 'Confirm',
        cancelLabel: 'Cancel',
        confirmStyle: 'danger',
        resolvePromise: null,
    });

    const showConfirm = useCallback((options = {}) => {
        return new Promise((resolve) => {
            setConfirmState({
                isOpen: true,
                title: options.title || 'Confirm Action',
                message: options.message || 'Are you sure you want to proceed?',
                confirmLabel: options.confirmLabel || 'Confirm',
                cancelLabel: options.cancelLabel || 'Cancel',
                confirmStyle: options.confirmStyle || 'danger',
                resolvePromise: resolve,
            });
        });
    }, []);

    const handleClose = useCallback(() => {
        if (confirmState.resolvePromise) {
            confirmState.resolvePromise(false);
        }
        setConfirmState(prev => ({ ...prev, isOpen: false }));
    }, [confirmState.resolvePromise]);

    const handleConfirm = useCallback(() => {
        if (confirmState.resolvePromise) {
            confirmState.resolvePromise(true);
        }
        setConfirmState(prev => ({ ...prev, isOpen: false }));
    }, [confirmState.resolvePromise]);

    return {
        confirmState,
        showConfirm,
        handleClose,
        handleConfirm,
    };
};

export default useConfirm;


